import { morphism } from 'morphism'
import { currencyUtils } from '../../../utils'
import {
  TDepositTransferMeta,
  TDepositTransferRequest,
  TDepositTransferResult,
  TPartyBalances,
  TPaymentHistory,
  TPayoutRequest,
  TPayoutResult,
  TRefundRequest,
  TRefundResult,
  TSuspenseAccount,
  TWalletBalance,
  TWalletBalanceAsset,
  TWalletTransaction,
  TPaymentRow,
  TWalletTransferRequest,
  TWireApprovedPayments,
  TWirePaymentHistory,
} from './types'

const { toCents, fromCents } = currencyUtils

/**
 * Every amount on the wire is in rands and every amount in the store is in cents; these
 * schemas are the only place the two meet. The request transformers return the wire shape,
 * which is structurally the same `T*` type with the amounts converted.
 */

const toCentsAmount = (amount: number | undefined) => toCents(amount).getAmount()

/**
 * Wallet Balance In
 */
const transformBalanceAssets = (assets: TWalletBalanceAsset[]): TWalletBalanceAsset[] =>
  assets.map(asset => ({
    ...asset,
    balance: toCentsAmount(asset.balance),
  }))

const transformWalletBalance = (balance: TWalletBalance): TWalletBalance => ({
  ...balance,
  assets: transformBalanceAssets(balance.assets),
})

const balanceSchema = {
  globalBalance: ({ globalBalance }: TPartyBalances) => transformWalletBalance(globalBalance),
  accountBalances: ({ accountBalances }: TPartyBalances) => accountBalances.map(transformWalletBalance),
  consolidatedBalance: ({ consolidatedBalance }: TPartyBalances) =>
    consolidatedBalance ? transformWalletBalance(consolidatedBalance) : undefined,
}

export const transformBalance = (source: TPartyBalances): TPartyBalances =>
  morphism(balanceSchema, source) as unknown as TPartyBalances

/**
 * Payments
 */
const paymentsSchema = {
  payments: ({ payments }: TWirePaymentHistory) =>
    payments.map(payment => ({
      ...payment,
      amount: toCentsAmount(payment.amount),
      approvedAt: new Date(payment.approvedAt),
      payoutAt: new Date(payment.payoutAt),
    })),
  period: 'period',
}

export const transformPayments = (source: TWirePaymentHistory): TPaymentHistory =>
  morphism(paymentsSchema, source) as unknown as TPaymentHistory

export const transformApprovedPayments = (source: TWireApprovedPayments): TPaymentRow[] =>
  source.payments.map(p => ({
    payment: p.paymentId,
    approvedAt: new Date(p.approvedAt),
    payoutAt: new Date(p.approvedAt),
    updatedAt: p.approvedAt,
    asset: 'zar#phoenix.coin',
    amount: toCentsAmount(p.amount),
    fromFullname: p.fromFullname ?? '',
    fromProperty: p.fromProperty,
    approvedByFullname: p.approvedByFullname ?? 'NA',
    beneficiaryTag: p.beneficiaryTag,
    paymentStatus: 'Approved',
    paymentKind: p.paymentKind,
    popAvailable: false,
    invoiceNumber: p.invoiceNumber,
  }))

/**
 * Payout
 */
const payoutRequestSchema = {
  party: 'party',
  asset: 'asset',
  amount: ({ amount }: TPayoutRequest) => fromCents(amount),
}

export const transformPayoutRequest = (source: TPayoutRequest): TPayoutRequest =>
  morphism(payoutRequestSchema, source) as unknown as TPayoutRequest

const payoutResponseSchema = {
  party: 'party',
  asset: 'asset',
  txHash: 'txHash',
  amount: 'amount',
}

export const transformPayoutResponse = (source: TPayoutResult): TPayoutResult =>
  morphism(payoutResponseSchema, source) as unknown as TPayoutResult

/**
 * Deposit Transfer
 */
const transferRequestSchema = {
  account: 'account',
  transferReason: 'transferReason',
  asset: 'asset',
  amount: ({ amount }: TDepositTransferRequest) => fromCents(amount),
}

export const transformTransferRequest = (source: TDepositTransferRequest): TDepositTransferRequest =>
  morphism(transferRequestSchema, source) as unknown as TDepositTransferRequest

/**
 * Wallet transfer
 */
const walletTransferRequestSchema = {
  fromAccount: 'fromAccount',
  fromParty: 'fromParty',
  agency: 'agency',
  transfers: ({ transfers }: TWalletTransferRequest) =>
    transfers.map(transfer => ({
      toAccount: transfer.toAccount,
      toParty: transfer.toParty,
      asset: transfer.asset,
      amount: fromCents(transfer.amount),
      description: transfer.description,
    })),
}

export const transformWalletTransferRequest = (source: TWalletTransferRequest): TWalletTransferRequest =>
  morphism(walletTransferRequestSchema, source) as unknown as TWalletTransferRequest

/** @todo remove this, not transforming anything? */
const transferResponseSchema = {
  txHash: 'res.txHash',
  meta: 'meta',
}

export const transformTransferResponse = (source: {
  res: TWalletTransaction | null
  meta: TDepositTransferMeta
}): TDepositTransferResult => morphism(transferResponseSchema, source) as unknown as TDepositTransferResult

/**
 * Refund
 */
const refundRequestSchema = {
  account: 'account',
  asset: 'asset',
  amount: ({ amount }: TRefundRequest) => fromCents(amount),
  reference: 'reference',
}

export const transformRefundRequest = (source: TRefundRequest): TRefundRequest =>
  morphism(refundRequestSchema, source) as unknown as TRefundRequest

const refundResponseSchema = {
  paymentId: 'paymentId',
}

export const transformRefundResponse = (source: TRefundResult): TRefundResult =>
  morphism(refundResponseSchema, source) as unknown as TRefundResult

/**
 * Withdraw
 */
const withdrawRequestSchema = {
  party: 'party',
  asset: 'asset',
  amount: ({ amount }: TPayoutRequest) => fromCents(amount),
  reference: 'reference',
  partyTag: 'partyTag',
  walletRef: 'walletRef',
}

export const transformWithdrawRequest = (source: TPayoutRequest): TPayoutRequest =>
  morphism(withdrawRequestSchema, source) as unknown as TPayoutRequest

const txHashSchema = {
  txHash: 'txHash',
}

export const transformWithdrawResponse = (source: TWalletTransaction): TWalletTransaction =>
  morphism(txHashSchema, source) as unknown as TWalletTransaction

/**
 * Consolidated Withdraw
 */
const consolidatedWithdrawRequestSchema = {
  party: 'party',
  asset: 'asset',
  amount: ({ amount }: TPayoutRequest) => fromCents(amount),
  reference: 'reference',
}

export const transformConsolidatedWithdrawRequest = (source: TPayoutRequest): TPayoutRequest =>
  morphism(consolidatedWithdrawRequestSchema, source) as unknown as TPayoutRequest

export const transformConsolidatedWithdrawResponse = (source: TWalletTransaction): TWalletTransaction =>
  morphism(txHashSchema, source) as unknown as TWalletTransaction

/**
 * Suspense accounts (`BadReferenceTable`)
 */
const getUnallocatedSuspenseAccountsSchema = {
  txId: 'txId',
  isAllocated: 'isAllocated',
  amount: ({ amount }: TSuspenseAccount) => toCentsAmount(amount),
  asset: 'asset',
  referenceUsed: 'referenceUsed',
  dateReceived: 'dateReceived',
  dateMonth: 'dateMonth',
  dateYear: 'dateYear',
}

export const transformGetUnallocatedSuspenseAccountsResponse = (source: TSuspenseAccount[]): TSuspenseAccount[] =>
  morphism(getUnallocatedSuspenseAccountsSchema, source as never) as unknown as TSuspenseAccount[]

const getAllocatedSuspenseAccountsSchema = {
  txId: 'txId',
  isAllocated: 'isAllocated',
  walletRef: 'walletRef',
  notes: 'notes',
  clickUpRef: 'clickUpRef',
  amount: ({ amount }: TSuspenseAccount) => toCentsAmount(amount),
  asset: 'asset',
  referenceUsed: 'referenceUsed',
  allocatedBy: 'allocatedBy',
  allocatedAt: 'allocatedAt',
  allocatedToRef: 'allocatedToRef',
  dateReceived: 'dateReceived',
  dateMonth: 'dateMonth',
  dateYear: 'dateYear',
}

export const transformGetAllocatedSuspenseAccountsResponse = (source: TSuspenseAccount[]): TSuspenseAccount[] =>
  morphism(getAllocatedSuspenseAccountsSchema, source as never) as unknown as TSuspenseAccount[]
